import PersonalizedDashboard from './components/dashboard/PersonalizedDashboard';
import CommunicationCentre from "./components/CommunicationCentre";
import ReleaseWelcome from "./components/ReleaseWelcome";
import {NotificationCentre,ClearanceRegistry} from './components/PlatformTools';
import {LandingPage,PublicClearanceCheck} from './components/PublicPortal';
import ResearcherTools from './components/ResearcherTools';
import CommitteeMeetings from './components/CommitteeMeetings';
// src/App.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AnimatePresence, motion, easeInOut } from 'framer-motion';
import { useAuth } from 'contexts/AuthContext';
import './App.css';

import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import ApplicationPage from './components/ApplicationPage';
import ApplicationForm from './components/ApplicationForm';
import ApprovalInbox from './components/ApprovalInbox';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import ThemeToggle from './components/ThemeToggle';
import UserManagement from './components/UserManagement';
import UserProfile from './components/UserProfile';
import Faculties from './components/Faculties';
import Departments from './components/Departments';
import ReportsPage from './components/ReportsPage';

import WorkflowConfiguration from './components/WorkflowConfiguration';
import SystemLogs from './components/SystemLogs';

interface LayoutProps {
  children: React.ReactNode;
}

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/* =========================
   Modern Preloader
========================= */
const Preloader: React.FC<{ exit: boolean }> = ({ exit }) => (
  <div className={`preloader logo-preloader ${exit ? 'preloader--out' : ''}`} role="status" aria-label="Loading your workspace">
    <div className="logo-preloader-mark"><span className="logo-preloader-halo" aria-hidden="true"/><img src="/logo2.png" alt="Research Ethics Automated System" /></div>
    <div className="logo-preloader-track" aria-hidden="true"><span/></div>
    <p>Preparing your workspace</p>
  </div>
);

/* =========================
   Page transitions
========================= */
const pageVariants = {
  initial: { opacity: 0, y: 4 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.18, ease: easeInOut },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: 0.15, ease: easeInOut },
  },
};

/* =========================
   Layout with preloader
========================= */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [showPreloader, setShowPreloader] = useState(true);
  const [exitPreloader, setExitPreloader] = useState(false);
  const [pageKey, setPageKey] = useState(location.pathname);

  useEffect(() => {
    setShowPreloader(true);
    setExitPreloader(false);

    let outroTimer: ReturnType<typeof setTimeout> | null = null;

    const introTimer = setTimeout(() => {
      setExitPreloader(true);

      outroTimer = setTimeout(() => {
        setShowPreloader(false);
        setPageKey(location.pathname);
        }, 100);
    }, 650);

    return () => {
      clearTimeout(introTimer);
      if (outroTimer) clearTimeout(outroTimer);
    };
  }, [location]);

  return (
    <>
      {showPreloader ? (
        <Preloader exit={exitPreloader} />
      ) : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={pageKey}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100%' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
};

/* =========================
   Routes
========================= */
const AppRoutes: React.FC = () => {
  const { isLoggedIn } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage/>}/>
      <Route path="/clearance-check" element={<PublicClearanceCheck/>}/>
      <Route path="/verify-clearance/:token" element={<PublicClearanceCheck/>}/>
      {isLoggedIn ? (
        <>
          <Route path="/notifications" element={<NotificationCentre/>}/>
          <Route path="/clearances" element={<ClearanceRegistry/>}/>
          <Route path="/action-centre" element={<ResearcherTools />} />
          <Route path="/committee-meetings" element={<CommitteeMeetings />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-applications" element={<PersonalizedDashboard applicantPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/applications" element={<ApplicationPage />} />
          <Route path="/applications/new" element={<ApplicationForm />} />
          <Route path="/applications/edit/:id" element={<ApplicationForm />} />
          <Route path="/approval-inbox" element={<ApprovalInbox />} />
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/user-profile" element={<UserProfile />} />
          <Route path="/faculties" element={<Faculties />} />
          <Route path="/departments" element={<Departments userRole="ADMIN" />} />
          <Route path="/messages" element={<CommunicationCentre />} />
          <Route path="/workflow-configuration" element={<WorkflowConfiguration />} />
          <Route path="/system-logs" element={<SystemLogs />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </>
      ) : (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </>
      )}
    </Routes>
  );
};

/* =========================
   Root App
========================= */
const App: React.FC = () => {
  const { isLoggedIn, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggingOutRef = useRef(false);

  const safeLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    try {
      logout();
    } finally {
      window.setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 300);
    }
  }, [logout]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();

    idleTimerRef.current = setTimeout(() => {
      safeLogout();
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, safeLogout]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearIdleTimer();
      return;
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleUserActivity = () => {
      startIdleTimer();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      startIdleTimer();
    };

    const handlePageExit = () => {
      safeLogout();
    };

    startIdleTimer();

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handlePageExit);
    window.addEventListener('pagehide', handlePageExit);

    return () => {
      clearIdleTimer();

      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handlePageExit);
      window.removeEventListener('pagehide', handlePageExit);
    };
  }, [isLoggedIn, startIdleTimer, clearIdleTimer, safeLogout]);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 768) {
      setIsMobileSidebarOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  };

  return (
    <BrowserRouter>
      <PublicLayoutGate>
      <ReleaseWelcome/>
      {isLoggedIn ? (
        <div className={`app-loggedin ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
          <Sidebar
            onSignOut={(e) => {
              e.preventDefault();
              safeLogout();
            }}
            isCollapsed={sidebarCollapsed}
            isMobileOpen={isMobileSidebarOpen}
            onToggleSidebar={handleToggleSidebar}
          />
          <div className="main-content">
            <Topbar onToggleSidebar={handleToggleSidebar} />
            <Layout>
              <AppRoutes />
            </Layout>
          </div>
        </div>
      ) : (
        <><div className="auth-theme-toggle"><ThemeToggle /></div><Layout>
          <AppRoutes />
        </Layout></>
      )}
      </PublicLayoutGate>
    </BrowserRouter>
  );
};

export default App;

function PublicLayoutGate({children}:{children:React.ReactNode}) {
  const {pathname}=useLocation();
  return pathname==='/'||pathname==='/clearance-check'||pathname.startsWith('/verify-clearance/')
    ? <><div className="auth-theme-toggle"><ThemeToggle/></div><AppRoutes/></> : <>{children}</>;
}
